/** User-facing sort modes for created-date lists (default: oldest first = latest last). */
export const CREATED_AT_SORT_OLDEST = 'oldest';
export const CREATED_AT_SORT_NEWEST = 'newest';
export const DEFAULT_CREATED_AT_SORT = CREATED_AT_SORT_OLDEST;

/** Lesson content (elements/exercises within a lesson). */
export const LESSON_CONTENT_SORT_LESSON_ORDER = 'lesson-order';
export const DEFAULT_LESSON_CONTENT_SORT = LESSON_CONTENT_SORT_LESSON_ORDER;

/** Lesson list sort modes — curriculum order or created date. */
export const LESSON_SORT_LESSON_NUMBER = 'lesson-number';
export const DEFAULT_LESSON_SORT = LESSON_SORT_LESSON_NUMBER;

/** Sort key for Mongo / API queries — oldest first (default list display). */
export const SORT_CREATED_AT_ASC = { createdAt: 1 };

/** Newest first — for explicit “latest on top” views. */
export const SORT_CREATED_AT_DESC = { createdAt: -1 };

/** Default API / library list ordering. */
export const SORT_CREATED_AT_DEFAULT = SORT_CREATED_AT_ASC;

export function toTimestampMs(v) {
  if (v == null) return 0;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isNaN(t) ? 0 : t;
  }
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function getCreatedAtMs(item) {
  return toTimestampMs(item?.createdAt);
}

/** Prefer junction link time when present (book/lesson content lists). */
export function getLessonDateMs(item) {
  const linked = toTimestampMs(item?.linkedAt);
  if (linked) return linked;
  return toTimestampMs(item?.createdAt);
}

/** Newest created first; stable tie-break on id. */
export function compareByCreatedAt(a, b) {
  const diff = getCreatedAtMs(b) - getCreatedAtMs(a);
  if (diff !== 0) return diff;
  const idA = String(a?.id ?? a?._id ?? '');
  const idB = String(b?.id ?? b?._id ?? '');
  return idA.localeCompare(idB);
}

/** Oldest created first (for progress / sequential course order). */
export function compareByCreatedAtAsc(a, b) {
  const diff = getCreatedAtMs(a) - getCreatedAtMs(b);
  if (diff !== 0) return diff;
  const idA = String(a?.id ?? a?._id ?? '');
  const idB = String(b?.id ?? b?._id ?? '');
  return idA.localeCompare(idB);
}

/** Comparator for a created-date sort mode (`oldest` | `newest`). */
export function getCreatedAtComparator(mode = DEFAULT_CREATED_AT_SORT) {
  return mode === CREATED_AT_SORT_NEWEST ? compareByCreatedAt : compareByCreatedAtAsc;
}

/** Sort by created date using `oldest` (default) or `newest`. */
export function sortByCreatedAtDirection(items, mode = DEFAULT_CREATED_AT_SORT) {
  return [...(items || [])].sort(getCreatedAtComparator(mode));
}

/** @deprecated Use sortByCreatedAtDirection — kept for call sites defaulting to newest-first. */
export function sortByCreatedAt(items) {
  return sortByCreatedAtDirection(items, CREATED_AT_SORT_NEWEST);
}

/** Sort using createdAt, falling back to updatedAt when needed (banks, audit, etc.). */
export function getListItemDateMs(item) {
  const created = getCreatedAtMs(item?.createdAt);
  if (created) return created;
  return getCreatedAtMs(item?.updatedAt);
}

export function compareByListItemDate(a, b, mode = DEFAULT_CREATED_AT_SORT) {
  const diff =
    mode === CREATED_AT_SORT_NEWEST
      ? getListItemDateMs(b) - getListItemDateMs(a)
      : getListItemDateMs(a) - getListItemDateMs(b);
  if (diff !== 0) return diff;
  const idA = String(a?.id ?? a?._id ?? '');
  const idB = String(b?.id ?? b?._id ?? '');
  return idA.localeCompare(idB);
}

export function sortByListItemDate(items, mode = DEFAULT_CREATED_AT_SORT) {
  return [...(items || [])].sort((a, b) => compareByListItemDate(a, b, mode));
}

/** Sort lessons by curriculum number or link/created date. */
export function sortLessonsByMode(items, mode = DEFAULT_LESSON_SORT) {
  if (mode === LESSON_SORT_LESSON_NUMBER) return sortByLessonNumber(items);
  const newest = mode === CREATED_AT_SORT_NEWEST;
  return [...(items || [])].sort((a, b) => {
    const dateDiff = newest
      ? getLessonDateMs(b) - getLessonDateMs(a)
      : getLessonDateMs(a) - getLessonDateMs(b);
    if (dateDiff !== 0) return dateDiff;
    const idA = String(a?.id ?? a?._id ?? '');
    const idB = String(b?.id ?? b?._id ?? '');
    return newest ? idB.localeCompare(idA) : idA.localeCompare(idB);
  });
}

/** Positive lesson `order` first (1, 2, 3…), then oldest linked/created first within a lesson. */
export function compareByLessonContentOrder(a, b) {
  const orderA = typeof a?.order === 'number' && a.order > 0 ? a.order : null;
  const orderB = typeof b?.order === 'number' && b.order > 0 ? b.order : null;
  if (orderA != null && orderB != null && orderA !== orderB) return orderA - orderB;
  if (orderA != null && orderB == null) return -1;
  if (orderA == null && orderB != null) return 1;

  const timeA = getLessonContentDateMs(a);
  const timeB = getLessonContentDateMs(b);
  if (timeA !== timeB) return timeA - timeB;

  const idA = String(a?.id ?? a?._id ?? '');
  const idB = String(b?.id ?? b?._id ?? '');
  return idA.localeCompare(idB);
}

export function sortByLessonContentOrder(items) {
  return [...(items || [])].sort(compareByLessonContentOrder);
}

/** Sort elements/exercises inside a lesson by curriculum order or linked/created date. */
export function getLessonContentDateMs(item) {
  return getLessonDateMs(item);
}

export function sortLessonContentByMode(items, mode = DEFAULT_LESSON_CONTENT_SORT) {
  if (mode === LESSON_CONTENT_SORT_LESSON_ORDER) return sortByLessonContentOrder(items);
  const newest = mode === CREATED_AT_SORT_NEWEST;
  return [...(items || [])].sort((a, b) => {
    const dateDiff = newest
      ? getLessonContentDateMs(b) - getLessonContentDateMs(a)
      : getLessonContentDateMs(a) - getLessonContentDateMs(b);
    if (dateDiff !== 0) return dateDiff;
    const idA = String(a?.id ?? a?._id ?? '');
    const idB = String(b?.id ?? b?._id ?? '');
    return newest ? idB.localeCompare(idA) : idA.localeCompare(idB);
  });
}

/**
 * Book-wide elements/exercises: curriculum order is lesson number, then content order within lesson.
 * `lessonsById` maps lessonId → lesson (needs lessonNumber).
 */
export function sortBookContentByMode(
  items,
  mode = DEFAULT_LESSON_CONTENT_SORT,
  lessonsById = new Map()
) {
  if (mode !== LESSON_CONTENT_SORT_LESSON_ORDER) {
    return sortLessonContentByMode(items, mode);
  }
  return [...(items || [])].sort((a, b) => {
    const lessonA = lessonsById.get(String(a?.lessonId));
    const lessonB = lessonsById.get(String(b?.lessonId));
    const na = lessonA?.lessonNumber;
    const nb = lessonB?.lessonNumber;
    if (na != null && nb != null && na !== nb) return na - nb;
    if (na != null && nb == null) return -1;
    if (na == null && nb != null) return 1;
    return compareByLessonContentOrder(a, b);
  });
}

/** Category buckets for student lesson content lists. */
export const LESSON_CONTENT_GROUP_KEYS = [
  'video',
  'audio',
  'downloadable-doc',
  'readable-content',
  'exercises',
];

/** Split a sorted flat list into category groups; section order follows first appearance in sort. */
export function groupLessonContentByCategory(items) {
  const groups = Object.fromEntries(LESSON_CONTENT_GROUP_KEYS.map((k) => [k, []]));
  const categoryOrder = [];
  const flatItems = [...(items || [])];
  for (const item of flatItems) {
    const key = item?.category === 'exercise' ? 'exercises' : item?.category;
    if (!groups[key]) continue;
    groups[key].push(item);
    if (!categoryOrder.includes(key)) categoryOrder.push(key);
  }
  return { groups, categoryOrder, flatItems };
}

/** Mongo sort for lesson junction rows: curriculum sequence within a lesson. */
export const SORT_LESSON_CONTENT_ORDER = { order: 1, createdAt: 1 };

/** Lessons stay ordered by lesson number (not created date). */
export function sortByLessonNumber(items) {
  return [...(items || [])].sort((a, b) => {
    const na = a?.lessonNumber;
    const nb = b?.lessonNumber;
    if (na != null && nb != null) return na - nb;
    if (na != null) return -1;
    if (nb != null) return 1;
    return compareByCreatedAtAsc(a, b);
  });
}

export function displayLessonNumber(lesson) {
  return lesson?.lessonNumber ?? null;
}
